import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Search,
  Download,
  Grid3X3,
  List,
  ChevronDown
} from 'lucide-react'
import Layout from '../components/Layout'
import Pagination from '../components/Pagination'
import ImageCard from '../components/ImageCard'
import DateTimePicker from '../components/DateTimePicker'
import MessageDialog from '../../shared/ui/MessageDialog'
import DownloadTypeModal from '../components/DownloadTypeModal'
import { useSatsueiDataKensaku } from '../hooks/useSatsueiDataKensaku'
import { useApiError } from '../../shared/hooks/useApiError'
import { satsueiDataKensakuService } from '../services/satsueiDataKensakuService'

const SatsueiDataKensaku = () => {
  const location = useLocation()
  const navigate = useNavigate()

  // Error handling
  const { showErrorDialog, errorMessage, messageType, closeErrorDialog, handleApiError, handleApiResponse, handleValidationErrors } = useApiError()

  // Use custom hook for search functionality
  const {
    searchParams,
    isLoading,
    searchResults,
    satsueiMemoCategories,
    kyotenList,
    pixUserList,
    loadInitialData,
    performSearch,
    downloadResults,
    clearSearchConditions,
    updateSearchParams
  } = useSatsueiDataKensaku({ handleApiError, handleApiResponse })

  // UI state
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false)
  const [showDownloadTypeModal, setShowDownloadTypeModal] = useState(false)
  const [displayType, setDisplayType] = useState('image') // 'image' or 'list'
  const [idSearchType, setIdSearchType] = useState('single') // 'single' or 'range'

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(30)

  // Ref to track if initial search from location state has been performed
  const hasPerformedInitialSearch = useRef(false)

  // Handle location state if available(ex: from Top page)
  useEffect(() => {
    if (location.state && location.state.satsueiDate && !hasPerformedInitialSearch.current && satsueiMemoCategories.length > 0) {
      hasPerformedInitialSearch.current = true
      
      const customSearchParams = {
        ...searchParams,
        ...(location.state.userId && { shokiTorokusha: location.state.userId }),
        ...(location.state.kyotenId !== undefined && location.state.kyotenId !== null && { kyotenId: location.state.kyotenId }),
        ...(location.state.satsueiDate && {
          shokiTorokuJikokuDateFrom: `${location.state.satsueiDate} 00:00`,
          shokiTorokuJikokuDateTo: `${location.state.satsueiDate} 23:59`,
          shokiTorokuJikokuJokenFrom: '5',
          shokiTorokuJikokuJokenTo: '5'
        })
      }
      
      // Update the search parameters to populate the form inputs
      updateSearchParams(customSearchParams)
      
      // Perform the search with the updated parameters
      performSearch(customSearchParams)
      
      // Clear the location state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location.pathname, navigate, performSearch, updateSearchParams, satsueiMemoCategories.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load initial data
  useEffect(() => {
    loadInitialData()
  }, [loadInitialData])

  // Validate date/time inputs when date/time specification is selected
  const validateDateTimeInputs = () => {
    if (searchParams.shokiTorokuJikokuJokenFrom === '5' && !searchParams.shokiTorokuJikokuDateFrom) {
      return false
    }
    if (searchParams.shokiTorokuJikokuJokenTo === '5' && !searchParams.shokiTorokuJikokuDateTo) {
      return false
    }
    if (searchParams.saishuKoshinJikokuJokenFrom === '5' && !searchParams.saishuKoshinJikokuDateFrom) {
      return false
    }
    if (searchParams.saishuKoshinJikokuJokenTo === '5' && !searchParams.saishuKoshinJikokuDateTo) {
      return false
    }
    return true
  }

  // Search function
  const handleSearch = () => {
    // Validate date/time inputs when date/time specification is selected
    if (!validateDateTimeInputs()) {
      handleValidationErrors('日時を入力してください。')
      return
    }
    
    performSearch()
    setCurrentPage(0)
    setShowAdvancedSearch(false)
  }

  // Download: open modal to choose type, then download with selected type
  const handleDownload = () => {
    setShowDownloadTypeModal(true)
  }
  const handleDownloadTypeConfirm = (downloadType) => {
    downloadResults(downloadType)
    setShowDownloadTypeModal(false)
  }

  // Navigation to detail page
  const handleObjectClick = (objectId) => {
    //navigate(`/satsueiDataToroku?objectId=${objectId}`)
    const url = `/satsueiDataToroku?objectId=${encodeURIComponent(objectId)}`;
    const title = "satsueiDataToroku" + Math.random();
    window.open(url, title);
  }

  // Clear all search conditions
  const handleClearSearchConditions = () => {
    clearSearchConditions()
    setCurrentPage(0)
  }

  // Pagination handlers
  const handlePageChange = (page) => setCurrentPage(page)
  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items)
    setCurrentPage(0)
  }

  // Check if any advanced search conditions are active
  const hasAdvancedSearchActive = () => {
    // Check initial registration user
    if (searchParams.shokiTorokusha) return true
    
    // Check initial registration time conditions
    // '1' = 指定なし (not specified), '5' = 日時指定 (date/time specified)
    if (searchParams.shokiTorokuJikokuJokenFrom === '5' && searchParams.shokiTorokuJikokuDateFrom) return true
    if (searchParams.shokiTorokuJikokuJokenTo === '2') return true // 現在時刻まで (current time)
    if (searchParams.shokiTorokuJikokuJokenTo === '5' && searchParams.shokiTorokuJikokuDateTo) return true
    
    // Check last update user
    if (searchParams.saishuKoshinsha) return true
    
    // Check last update time conditions
    if (searchParams.saishuKoshinJikokuJokenFrom === '5' && searchParams.saishuKoshinJikokuDateFrom) return true
    if (searchParams.saishuKoshinJikokuJokenTo === '2') return true // 現在時刻まで (current time)
    if (searchParams.saishuKoshinJikokuJokenTo === '5' && searchParams.saishuKoshinJikokuDateTo) return true
    
    return false
  }

  // Calculate pagination
  const totalCount = searchResults.length
  const totalPages = Math.ceil(totalCount / itemsPerPage)
  const startIndex = currentPage * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentResults = searchResults.slice(startIndex, endIndex)

  return (
    <Layout pageTitle="撮影データ検索一覧">
      <div className="space-y-4">
        {/* Search Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Left side - Basic search */}
            <div className="flex-1 lg:flex-[9] space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {/* ID Search Section */}
                  <div className="sm:col-span-2 lg:col-span-1 xl:col-span-1">
                    <div className="flex flex-row gap-2">
                      <div className="flex flex-col space-y-1">
                        <label className={`flex items-center justify-center text-xs p-2 border rounded cursor-pointer transition-colors ${idSearchType === 'single'
                            ? 'bg-red-100 border-red-300 text-red-700'
                            : 'bg-white border-gray-300 hover:bg-gray-50'
                          }`}>
                          <input
                            type="radio"
                            name="idSearchType"
                            value="single"
                            checked={idSearchType === 'single'}
                            onChange={(e) => {
                              setIdSearchType(e.target.value)
                              // Clear range search inputs when switching to single
                              updateSearchParams({ objectIdFrom: '', objectIdTo: '' })
                            }}
                            className="sr-only"
                          />
                          ID検索
                        </label>
                        <label className={`flex items-center justify-center text-xs p-2 border rounded cursor-pointer transition-colors ${idSearchType === 'range'
                            ? 'bg-red-100 border-red-300 text-red-700'
                            : 'bg-white border-gray-300 hover:bg-gray-50'
                          }`}>
                          <input
                            type="radio"
                            name="idSearchType"
                            value="range"
                            checked={idSearchType === 'range'}
                            onChange={(e) => {
                              setIdSearchType(e.target.value)
                              // Clear single search input when switching to range
                              updateSearchParams({ objectId: '' })
                            }}
                            className="sr-only"
                          />
                          ID範囲検索
                        </label>
                      </div>
                      <div className="flex-1">
                        {idSearchType === 'single' ? (
                          <textarea
                            id="objectId"
                            data-testid="object-id-textarea"
                            placeholder="ID"
                            rows="2"
                            className="h-full w-full border border-gray-300 rounded px-2 py-1.5 resize-none text-sm"
                            value={searchParams.objectId}
                            onChange={(e) => updateSearchParams({ objectId: e.target.value })}
                          />
                        ) : (
                          <div className="space-y-1">
                            <input
                              id="objectIdFrom"
                              data-testid="object-id-from-input"
                              type="text"
                              placeholder="FROM"
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                              value={searchParams.objectIdFrom}
                              onChange={(e) => updateSearchParams({ objectIdFrom: e.target.value })}
                            />
                            <input
                              id="objectIdTo"
                              data-testid="object-id-to-input"
                              type="text"
                              placeholder="TO"
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                              value={searchParams.objectIdTo}
                              onChange={(e) => updateSearchParams({ objectIdTo: e.target.value })}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Category */}
                  <div className="space-y-1">
                    <label htmlFor="satsueiMemoCategoryId" className="block text-sm font-medium text-gray-700 mb-1">
                      撮影カテゴリー
                    </label>
                    <select
                      id="satsueiMemoCategoryId"
                      data-testid="satsuei-memo-category-select"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={searchParams.satsueiMemoCategoryId}
                      onChange={(e) => updateSearchParams({ satsueiMemoCategoryId: e.target.value })}
                    >
                      <option value="">---</option>
                      {satsueiMemoCategories.map(category => (
                        <option key={category.satsueiMemoCategoryId} value={category.satsueiMemoCategoryId}>
                          {category.satsueiMemoCategoryName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Location */}
                  <div className="space-y-1">
                    <label htmlFor="kyotenId" className="block text-sm font-medium text-gray-700 mb-1">
                      拠点
                    </label>
                    <select
                      id="kyotenId"
                      data-testid="kyoten-select"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={searchParams.kyotenId}
                      onChange={(e) => updateSearchParams({ kyotenId: e.target.value })}
                    >
                      <option value="">---</option>
                      <option value="0">未指定</option>
                      {kyotenList.map(kyoten => (
                        <option key={kyoten.kyotenId} value={kyoten.kyotenId}>
                          {kyoten.kyotenName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sort */}
                  <div className="space-y-1">
                    <label htmlFor="sort" className="block text-sm font-medium text-gray-700 mb-1">
                      ソート順
                    </label>
                    <select
                      id="sort"
                      data-testid="sort-select"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={searchParams.sort}
                      onChange={(e) => updateSearchParams({ sort: e.target.value })}
                    >
                      <option value="1">ID</option>
                      <option value="2">最終更新時刻</option>
                    </select>
                  </div>

                  {/* Options */}
                  <div className="sm:col-span-2 lg:col-span-1 xl:col-span-1 space-y-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">オプション</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-1 gap-1">
                      <label htmlFor="noImageRegist" className="flex items-center text-sm">
                        <input
                          id="noImageRegist"
                          type="checkbox"
                          checked={searchParams.noImageRegist}
                          onChange={(e) => updateSearchParams({ noImageRegist: e.target.checked })}
                          className="mr-1"
                        />
                        画像未撮影
                      </label>
                      <label htmlFor="imageRegist" className="flex items-center text-sm">
                        <input
                          id="imageRegist"
                          type="checkbox"
                          checked={searchParams.imageRegist}
                          onChange={(e) => updateSearchParams({ imageRegist: e.target.checked })}
                          className="mr-1"
                        />
                        画像撮影済
                      </label>
                      <label htmlFor="noSatsueiMemoRegist" className="flex items-center text-sm">
                        <input
                          id="noSatsueiMemoRegist"
                          type="checkbox"
                          checked={searchParams.noSatsueiMemoRegist}
                          onChange={(e) => updateSearchParams({ noSatsueiMemoRegist: e.target.checked })}
                          className="mr-1"
                        />
                        商品情報未登録
                      </label>
                    </div>
                  </div>
                </div>
              </div>

            {/* Right side - Display type buttons only */}
            <div className="flex flex-col justify-center space-y-2 lg:flex-[1]">
              <div className="space-y-2">
                <div className="flex space-x-1">
                  <button
                    onClick={handleSearch}
                    disabled={isLoading}
                    className="flex-[4] bg-red-600 text-white px-3 py-1.5 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-sm"
                  >
                    {isLoading ? '検索中...' : '検索'}
                  </button>
                  {searchResults.length > 0 && (
                    <button
                      onClick={handleDownload}
                      disabled={isLoading}
                      className="flex-[1] bg-blue-600 text-white p-1.5 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                      title="ダウンロード"
                    >
                      <Download className="h-3 w-3 mx-auto" />
                    </button>
                  )}
                </div>
                <div className="flex">
                  <button
                    onClick={handleClearSearchConditions}
                    className="flex-1 text-gray-600 hover:text-gray-800 p-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50"
                    title="検索条件クリア"
                  >
                    <span className="text-xs">検索条件クリア</span>
                  </button>
                </div>
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => setDisplayType('image')}
                  className={`flex-1 flex items-center justify-center p-2 border rounded text-sm ${displayType === 'image'
                    ? 'bg-red-100 border-red-300 text-red-700'
                    : 'bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                  title="画像一覧"
                >
                  <Grid3X3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setDisplayType('list')}
                  className={`flex-1 flex items-center justify-center p-2 border rounded text-sm ${displayType === 'list'
                    ? 'bg-red-100 border-red-300 text-red-700'
                    : 'bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                  title="リスト表示"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Advanced Search Accordion */}
          <div className="mt-2">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors duration-200 text-sm px-2 py-1"
              >
                <div className={`transition-transform duration-300 ${showAdvancedSearch ? 'rotate-180' : 'rotate-0'}`}>
                  <ChevronDown className="h-4 w-4" />
                </div>
                <span>詳細条件</span>
              </button>
              {hasAdvancedSearchActive() && (
                <div className="text-xs text-red-600 font-medium bg-red-50 px-2 py-1 rounded border border-red-200">
                  詳細条件選択中
                </div>
              )}
            </div>

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${showAdvancedSearch
                ? 'max-h-[2000px] opacity-100 mt-4'
                : 'max-h-0 opacity-0 mt-0'
                }`}
            >
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Initial Registration */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-800">初期登録</h4>

                    <div>
                      <label htmlFor="shokiTorokusha" className="block text-sm font-medium text-gray-700 mb-1">
                        初期登録者
                      </label>
                      <select
                        id="shokiTorokusha"
                        data-testid="shoki-torokusha-select"
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        value={searchParams.shokiTorokusha}
                        onChange={(e) => updateSearchParams({ shokiTorokusha: e.target.value })}
                      >
                        <option value="">---</option>
                        {pixUserList.map(user => (
                          <option key={user.pixUserId} value={user.pixUserId}>
                            {user.userName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label htmlFor="shokiTorokuJikokuJokenFrom" className="block text-sm font-medium text-gray-700 mb-1">
                          初期登録時刻 から
                        </label>
                        <select
                          id="shokiTorokuJikokuJokenFrom"
                          data-testid="shoki-toroku-jikoku-joken-from-select"
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                          value={searchParams.shokiTorokuJikokuJokenFrom}
                          onChange={(e) => updateSearchParams({ shokiTorokuJikokuJokenFrom: e.target.value })}
                        >
                          <option value="1">指定なし</option>
                          <option value="5">日時指定から</option>
                        </select>
                        {searchParams.shokiTorokuJikokuJokenFrom === '5' && (
                          <div className="mt-2">
                            <DateTimePicker
                              value={searchParams.shokiTorokuJikokuDateFrom}
                              onChange={(value) => updateSearchParams({ shokiTorokuJikokuDateFrom: value })}
                              placeholder="開始日時"
                            />
                          </div>
                        )}
                      </div>
                      <div>
                        <label htmlFor="shokiTorokuJikokuJokenTo" className="block text-sm font-medium text-gray-700 mb-1">
                          初期登録時刻 まで
                        </label>
                        <select
                          id="shokiTorokuJikokuJokenTo"
                          data-testid="shoki-toroku-jikoku-joken-to-select"
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                          value={searchParams.shokiTorokuJikokuJokenTo}
                          onChange={(e) => updateSearchParams({ shokiTorokuJikokuJokenTo: e.target.value })}
                        >
                          <option value="1">指定なし</option>
                          <option value="2">現在時刻まで</option>
                          <option value="5">日時指定まで</option>
                        </select>
                        {searchParams.shokiTorokuJikokuJokenTo === '5' && (
                          <div className="mt-2">
                            <DateTimePicker
                              value={searchParams.shokiTorokuJikokuDateTo}
                              onChange={(value) => updateSearchParams({ shokiTorokuJikokuDateTo: value })}
                              placeholder="終了日時"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Last Update */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-800">最終更新</h4>

                    <div>
                      <label htmlFor="saishuKoshinsha" className="block text-sm font-medium text-gray-700 mb-1">
                        最終更新者
                      </label>
                      <select
                        id="saishuKoshinsha"
                        data-testid="saishu-koshinsha-select"
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        value={searchParams.saishuKoshinsha}
                        onChange={(e) => updateSearchParams({ saishuKoshinsha: e.target.value })}
                      >
                        <option value="">---</option>
                        {pixUserList.map(user => (
                          <option key={user.pixUserId} value={user.pixUserId}>
                            {user.userName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          最終更新時刻 から
                        </label>
                        <select
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                          value={searchParams.saishuKoshinJikokuJokenFrom}
                          onChange={(e) => updateSearchParams({ saishuKoshinJikokuJokenFrom: e.target.value })}
                        >
                          <option value="1">指定なし</option>
                          <option value="5">日時指定から</option>
                        </select>
                        {searchParams.saishuKoshinJikokuJokenFrom === '5' && (
                          <div className="mt-2">
                            <DateTimePicker
                              value={searchParams.saishuKoshinJikokuDateFrom}
                              onChange={(value) => updateSearchParams({ saishuKoshinJikokuDateFrom: value })}
                              placeholder="開始日時"
                            />
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          最終更新時刻 まで
                        </label>
                        <select
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                          value={searchParams.saishuKoshinJikokuJokenTo}
                          onChange={(e) => updateSearchParams({ saishuKoshinJikokuJokenTo: e.target.value })}
                        >
                          <option value="1">指定なし</option>
                          <option value="2">現在時刻まで</option>
                          <option value="5">日時指定まで</option>
                        </select>
                        {searchParams.saishuKoshinJikokuJokenTo === '5' && (
                          <div className="mt-2">
                            <DateTimePicker
                              value={searchParams.saishuKoshinJikokuDateTo}
                              onChange={(value) => updateSearchParams({ saishuKoshinJikokuDateTo: value })}
                              placeholder="終了日時"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results Section */}
        {searchResults.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageStart={Math.max(0, currentPage - 2)}
              pageEnd={Math.min(totalPages - 1, currentPage + 2)}
              totalCount={totalCount}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onFirstPage={() => setCurrentPage(0)}
              onLastPage={() => setCurrentPage(totalPages - 1)}
              onPreviousPage={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              onNextPage={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
              onItemsPerPageChange={handleItemsPerPageChange}
              showItemsPerPage={true}
            />

            {/* Results Display */}
            {displayType === 'image' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4 mt-4">
                {currentResults.map((item) => (
                  <div
                    key={item.objectId}
                    onClick={() => handleObjectClick(item.objectId)}
                    className="cursor-pointer"
                  >
                    <ImageCard 
                      image={item} 
                      imageService={satsueiDataKensakuService}
                      pageType="satsueiDataKensaku"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto mt-4">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        画像
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        撮影カテゴリー
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        拠点
                      </th>
                      {/* Dynamic columns for satsuei category 0 - always show */}
                      {currentResults.length > 0 && currentResults[0].columnList
                        ?.filter(col => col.columnKey.startsWith('col_0_') && col.columnType !== 1)
                        .map(column => (
                          <th key={column.columnKey} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {column.columnName}
                          </th>
                        ))
                      }
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        初期登録者
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        初期登録時刻
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        最終更新者
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        最終更新時刻
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentResults.map((item) => (
                      <tr
                        key={item.objectId}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleObjectClick(item.objectId)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.objectId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="w-40 h-40 overflow-hidden">
                            <ImageCard 
                              image={item} 
                              className="w-40 h-40"
                              imageService={satsueiDataKensakuService}
                              pageType="satsueiDataKensaku"
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.satsueiMemoCategoryName || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.kyotenName || '未指定'}
                        </td>
                        {/* Dynamic columns data for satsuei category 0 - always show */}
                        {currentResults.length > 0 && currentResults[0].columnList
                          ?.filter(col => col.columnKey.startsWith('col_0_') && col.columnType !== 1)
                          .map(column => {
                            const columnData = item.columnList?.find(col => col.columnKey === column.columnKey)
                            return (
                              <td key={column.columnKey} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {columnData?.columnValue || '-'}
                              </td>
                            )
                          })
                        }
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.createUserName || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.createDatetime || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.updateUserName || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.updateDatetime || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && searchResults.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">検索条件を入力して検索してください</p>
          </div>
        )}
      </div>

      {/* Download type modal */}
      <DownloadTypeModal
        isOpen={showDownloadTypeModal}
        onClose={() => setShowDownloadTypeModal(false)}
        onConfirm={handleDownloadTypeConfirm}
      />

      {/* Error Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title={messageType === 'error' ? 'エラー' : messageType === 'success' ? '成功' : messageType === 'warning' ? '警告' : '情報'}
        message={errorMessage}
        buttonText="閉じる"
        type={messageType}
      />
    </Layout>
  )
}

export default SatsueiDataKensaku
